import React, { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import TextField from '@mui/material/TextField';
import ExpandMoreIcon from '../icons/ExpandMoreIcon';
import CloseIcon from '../icons/CloseIcon';
import SearchIcon from '../icons/SearchIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import { softFill, hairline, floatSurfaceSx, railItemSx, scrollY, filterPillSx } from '../styles';

// The standard admin filter control: a pill (leading icon +
// label, hairline border, quiet hover) that opens a searchable popover to pick
// a value. Use this for ANY list/table filter so they all read the same —
// don't hand-roll a bare MUI Autocomplete for filtering.
//
// Props mirror the option-list shape the app already uses:
//   icon            leading icon node (optional)
//   label           the filter's name, e.g. "City"
//   value           selected option (single mode) or array of options (multiple)
//   options         array of option objects
//   getOptionLabel  option -> display string (default: String(option))
//   onChange        single: (value|null) => void; multiple: (array) => void
//   placeholder     shown on the pill when nothing is selected (default: label)
//   multiple        checklist mode — value is an array, popover stays open (default: false)
//   searchable      show the search box in the popover (default: true)
//   clearLabel      the "reset" row label (default: `All ${label}`)
//   renderOption    option -> node for the option's row, when a plain label isn't
//                   enough (e.g. a status icon beside the name). getOptionLabel is
//                   still what the pill summarises and the search box matches on.
//   width           popover width (default 280)
//   inlineLabel     show `label · value` on the pill (default true). Pass false
//                   when the filter already has a heading above it, so the pill
//                   reads "Honda" rather than "Make · Honda". label is still
//                   used for the placeholder and the clear button's aria-label.
const FilterSelect = ({
  icon,
  label,
  value = null,
  options = [],
  getOptionLabel = (o) => String(o),
  getOptionKey,
  onChange,
  placeholder,
  multiple = false,
  searchable = true,
  clearLabel,
  renderOption,
  width = 280,
  inlineLabel = true,
}) => {
  // Identity for selection/toggle. Defaults to the label, but callers with
  // options that can share a label should pass an explicit key so toggling one
  // doesn't silently affect another.
  const keyOf = getOptionKey || getOptionLabel;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedList = multiple ? (Array.isArray(value) ? value : []) : [];
  const active = multiple ? selectedList.length > 0 : value != null;

  // Text shown after the label on the pill when something is selected.
  const summary = multiple
    ? selectedList.length === 1
      ? getOptionLabel(selectedList[0])
      : `${selectedList.length} selected`
    : value != null
    ? getOptionLabel(value)
    : '';

  const isSelected = (option) => {
    const optKey = keyOf(option);
    return multiple
      ? selectedList.some((v) => keyOf(v) === optKey)
      : value != null && keyOf(value) === optKey;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(q));
  }, [options, query, getOptionLabel]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const clearSelection = () => onChange && onChange(multiple ? [] : null);

  const pick = (option) => {
    if (option === null) {
      clearSelection();
      close();
      return;
    }
    if (multiple) {
      // Toggle the option and keep the popover open for further picks.
      const optKey = keyOf(option);
      const next = selectedList.some((v) => keyOf(v) === optKey)
        ? selectedList.filter((v) => keyOf(v) !== optKey)
        : [...selectedList, option];
      onChange && onChange(next);
      return;
    }
    onChange && onChange(option);
    close();
  };

  return (
    <>
      {/* Pill wrapper is a plain container, not a button, so the trigger and the
          clear control can be sibling <button>s — nesting an interactive element
          inside a <button> is invalid HTML and creates two tab stops in one. */}
      <Box
        ref={anchorRef}
        sx={{
          ...filterPillSx,
          px: 0,
          gap: 0,
          maxWidth: 320,
          cursor: 'default',
          ...(active && {
            backgroundColor: softFill,
            borderColor: (theme) => theme.palette.text.inactive,
          }),
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: 0,
            height: '100%',
            py: 0,
            pl: '12px',
            pr: active ? '4px' : '12px',
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            cursor: 'pointer',
          }}
        >
          {icon && (
            <Box component="span" sx={{ display: 'flex', color: 'text.secondary', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
              {icon}
            </Box>
          )}
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
            {(!active || inlineLabel) && (
              <Box component="span" sx={{ color: active ? 'text.secondary' : 'text.primary', flexShrink: 0 }}>
                {active ? label : placeholder || label}
              </Box>
            )}
            {active && (
              <>
                {inlineLabel && (
                  <Box component="span" sx={{ color: 'text.inactive', flexShrink: 0 }}>
                    ·
                  </Box>
                )}
                <Box
                  component="span"
                  sx={{ color: 'text.primary', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {summary}
                </Box>
              </>
            )}
          </Box>
          {!active && <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.inactive' }} />}
        </Box>
        {active && (
          <Box
            component="button"
            type="button"
            aria-label={`Clear ${label} filter`}
            onClick={clearSelection}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              pl: '2px',
              pr: '10px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              '& .MuiSvgIcon-root': { fontSize: 15 },
            }}
          >
            <CloseIcon />
          </Box>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: '8px', width, ...floatSurfaceSx } }}
      >
        {searchable && (
          <Box sx={{ p: '10px', borderBottom: (theme) => `1px solid ${hairline(theme)}` }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={`Search ${label.toLowerCase()}`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 17, mr: '6px', color: 'text.secondary' }} /> }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '999px',
                  fontSize: '14px',
                  backgroundColor: softFill,
                  fieldset: { border: 'none' },
                },
                '& input::placeholder': { color: 'text.inactive', opacity: 1 },
              }}
            />
          </Box>
        )}
        <Box sx={{ maxHeight: 280, p: '6px', ...scrollY }}>
          <List disablePadding>
            <ListItemButton disableRipple onClick={() => pick(null)} selected={!active} sx={railItemSx}>
              {clearLabel || `All ${label.toLowerCase()}`}
            </ListItemButton>
            {filtered.map((option) => {
              const optLabel = getOptionLabel(option);
              const rowSelected = isSelected(option);
              return (
                <ListItemButton
                  key={String(keyOf(option))}
                  disableRipple
                  selected={!multiple && rowSelected}
                  onClick={() => pick(option)}
                  sx={{ ...railItemSx, display: 'flex', justifyContent: 'space-between', gap: '8px' }}
                >
                  {renderOption ? (
                    renderOption(option)
                  ) : (
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {optLabel}
                    </Box>
                  )}
                  {multiple && rowSelected && (
                    <CheckCircleIcon sx={{ fontSize: 17, color: 'text.primary', flexShrink: 0 }} />
                  )}
                </ListItemButton>
              );
            })}
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: '12px', py: '10px', fontSize: '13px' }}>
                No matches.
              </Typography>
            )}
          </List>
        </Box>
        {/* Multi-select keeps the popover open on each pick, so it needs a way
            out that isn't "click somewhere else". Single-select closes on pick
            and doesn't. */}
        {multiple && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: '8px 10px',
              borderTop: (theme) => `1px solid ${hairline(theme)}`,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={close}
              sx={{
                ...filterPillSx,
                height: '30px',
                border: 'none',
                fontWeight: 600,
                backgroundColor: softFill,
                '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
              }}
            >
              Done
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default FilterSelect;
